import { urlMaker } from '@shared/utils/url-maker'
import { caller } from '@shared/utils/caller'
import { ApiResponse } from '@shared/utils/api-response'
import { Warehouse, WarehouseFormData, WarehouseFormDataApi, WarehouseFormErrors, WarehouseSelect, WarehouseUpdateState } from '../types'
import { WarehouseMappers } from '../mappers'
import { appendToFormData } from '@shared/utils/apply-to-form-data'
import { NextTableMetaResponse } from '@shared/modules/next-table/types'
import { nextTableMetaMapper } from '@shared/modules/next-table/utils/meta-mapper'

export class WarehousesService {
  public static async getAll(
    query?: Record<string, any>,
  ): Promise<ApiResponse<200, Warehouse[], NextTableMetaResponse> | ApiResponse<400 | 500, string>> {
    const url = urlMaker('/api/warehouses', query)

    try {
      const response = await caller(url)

      if (response.ok) {
        const { data, ...meta } = await response.json()
        const mappedData = WarehouseMappers.manyFromApi(data)
        return new ApiResponse(200, mappedData, nextTableMetaMapper(meta.meta))
      } else {
        return new ApiResponse(400, 'Məlumatlar əldə edilə bilmədi', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi', null)
    }
  }

  public static async getSelect(query?: Record<string, any>): Promise<ApiResponse<200, WarehouseSelect[]> | ApiResponse<400 | 500, string>> {
    const url = urlMaker('/api/warehouses/select', query)

    try {
      const response = await caller(url)

      if (response.ok) {
        const result = await response.json()
        const mappedData = WarehouseMappers.manyFromSelectApi(result)
        return new ApiResponse(200, mappedData, null)
      } else {
        return new ApiResponse(400, 'Məlumatlar əldə edilə bilmədi', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi', null)
    }
  }

  public static async getOneById(id: string | number): Promise<ApiResponse<200, Warehouse> | ApiResponse<400 | 500, string>> {
    const url = urlMaker('/api/warehouses/info', { id })
    try {
      const response = await caller(url)

      if (response.ok) {
        const { data } = await response.json()
        return new ApiResponse(200, WarehouseMappers.fromApi(data), null)
      } else {
        return new ApiResponse(400, 'Məlumatlar əldə edilə bilmədi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null)
    }
  }

  public static async createOrUpdate(
    values: WarehouseFormData,
    type: string = 'create',
  ): Promise<ApiResponse<200> | ApiResponse<422, WarehouseFormErrors> | ApiResponse<400 | 500, string>> {
    const url = type === 'create' ? urlMaker('/api/warehouses/create') : urlMaker(`/api/warehouses/edit`)
    const body = new FormData()

    appendToFormData<WarehouseFormDataApi>(WarehouseMappers.formDataToApi(values), body)

    try {
      const response = await caller(url, { body, method: 'POST' })

      if (response.ok) {
        return new ApiResponse(200, null, null)
      } else if (response.status === 422) {
        const { errors } = await response.json()

        return new ApiResponse(422, WarehouseMappers.formErrorsFromApi(errors), null)
      } else {
        return new ApiResponse(400, 'Əməliyyat aparıla bilmədi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null)
    }
  }

  public static async deleteById(id: string | number): Promise<ApiResponse<200, string> | ApiResponse<500, string>> {
    try {
      const url = urlMaker('/api/warehouses/remove')
      const body = new FormData()

      appendToFormData({ id }, body)
      const response = await caller(url, { method: 'POST', body })
      if (!response.ok) {
        return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
      } else {
        return new ApiResponse(200, 'Anbar uğurla silindi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
    }
  }

  public static async updateStateById(values: WarehouseUpdateState): Promise<ApiResponse<200, string> | ApiResponse<500, string>> {
    try {
      const url = urlMaker('/api/warehouses/update_state')
      const body = new FormData()

      appendToFormData(values, body)
      const response = await caller(url, { method: 'POST', body })
      if (!response.ok) {
        return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
      } else {
        return new ApiResponse(200, 'Anbarın statusu uğurla dəyişdirildi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
    }
  }
}
